import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChatList, useMeQuery } from '@tradeoutlet/data';
import type { MessageTaskLink, Team } from '@tradeoutlet/shared';
import { membersById } from '@tradeoutlet/shared';
import { api } from './api';
import { qk } from './queryClient';
import { useUi } from './store';
import { useAuth } from './auth';

export function useMe() {
  const { status } = useAuth();
  return useMeQuery(status === 'signed_in');
}

/** Current workspace id (persisted; defaults to the first workspace). */
export function useWorkspaceId(): string | null {
  const me = useMe();
  const ws = useUi(s => s.workspaceId);
  const setWs = useUi(s => s.setWorkspace);
  const valid = me.data?.workspaces.some(w => w.id === ws);
  useEffect(() => {
    if (me.data && !valid && me.data.workspaces[0]) setWs(me.data.workspaces[0].id);
  }, [me.data, valid, setWs]);
  return valid ? ws : me.data?.workspaces[0]?.id ?? null;
}

export function useWorkspace() {
  const me = useMe();
  const id = useWorkspaceId();
  return me.data?.workspaces.find(w => w.id === id) ?? null;
}

export function useMembers() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: qk.members(ws ?? ''), queryFn: () => api.members(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
  const byId = useMemo(() => membersById(q.data?.members ?? [], q.data?.deleted, ws ?? ''), [q.data, ws]);
  return { ...q, members: q.data?.members ?? [], byId };
}

export function useConversations() {
  const ws = useWorkspaceId();
  return useChatList(ws);
}

export function useProjects() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: qk.projects(ws ?? ''), queryFn: () => api.projects(ws!), enabled: Boolean(ws) });
}

export function useWfContext() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: qk.wf(ws ?? '', 'context'), queryFn: () => api.wf.context(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Buildings to offer in a picker: archived ones are left out, except the one already chosen (#60). */
export function pickableLocations<T extends { id: string; archived_at?: string | null }>(list: T[], current?: string | null): T[] {
  return list.filter(l => !l.archived_at || (current && l.id === current));
}

export function useLocations() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['locations', ws], queryFn: () => api.locations(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
}

export function usePositions() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: qk.wf(ws ?? '', 'positions'), queryFn: () => api.wf.positions(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 });
}

export function useChatSections() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: qk.chatSections(ws ?? ''), queryFn: () => api.chatSections(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Departments + groups (both live in collab.teams). */
export function useTeams() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: qk.teams(ws ?? ''), queryFn: () => api.teams(ws!), enabled: Boolean(ws), staleTime: 60_000 });
  const byId = useMemo(() => new Map<string, Team>((q.data?.teams ?? []).map(t => [t.id, t])), [q.data]);
  return { ...q, teams: q.data?.teams ?? [], byId };
}

/**
 * Task chips for a conversation, keyed by message id. The conversation page passes `poll` so a
 * single observer refreshes them (realtime 'task.links_changed' events also invalidate the query).
 */
export function useTaskLinks(conversationId: string | null | undefined, poll = false) {
  const q = useQuery({
    queryKey: qk.taskLinks(conversationId ?? ''), queryFn: () => api.taskLinks(conversationId!), enabled: Boolean(conversationId),
    staleTime: 30_000, refetchInterval: poll ? 15_000 : false,
  });
  return useMemo(() => {
    const m = new Map<string, MessageTaskLink[]>();
    for (const l of q.data?.links ?? []) m.set(l.message_id, [...(m.get(l.message_id) ?? []), l]);
    return m;
  }, [q.data]);
}

export function useDisplayName() {
  const { byId } = useMembers();
  return (id: string | null | undefined) => (id ? byId.get(id)?.display_name ?? 'Unknown' : 'Unknown');
}

export function conversationName(c: { kind: string; title: string | null; slug: string | null; members?: { user_id: string; display_name: string }[] }, me?: string | null) {
  if (c.kind === 'channel') return c.slug ?? c.title ?? 'channel';
  if (c.title) return c.title;
  const others = (c.members ?? []).filter(m => m.user_id !== me).map(m => m.display_name);
  return others.length ? others.join(', ') : 'Just you';
}
